// src/components/WalletIcons.js

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback
} from "react";

import { useLocation } from "react-router-dom";

import {
  Wallet,
  Send,
  Settings,
  Menu,
  LayoutDashboard,
  TrendingUp,
  ArrowLeftRight,
  GitBranch,
  Copy,
  X,
  CheckCircle,
  Download,
  ShoppingBag,
  Activity
} from "lucide-react";

import "./WalletIcons.css";


// ============================================================
// GOLDCHAIN CONFIGURATION
// ============================================================

const GOLDCHAIN_RPC = "https://rpc.goldchain.tech";

const GOLDCHAIN_CHAIN_ID = "0x777771";


// ============================================================
// BAIT PRICE API
// ============================================================

const BAIT_PRICE_API =
  "https://goldapi.bait-coin.com/api/gold-reference";


// ============================================================
// COMPONENT
// ============================================================

const WalletIcons = ({
  wallet,
  baitPrice,
  selectedChain,
  onNavigate,
  activeItem,
  balance
}) => {

  const location = useLocation();


  // ==========================================================
  // SIDEBAR STATE
  // ==========================================================

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [copied, setCopied] = useState(false);


  // ==========================================================
  // BLOCKCHAIN BALANCE
  // ==========================================================

  const [rpcBalance, setRpcBalance] = useState(null);

  const [balanceLoading, setBalanceLoading] =
    useState(false);

  const [balanceError, setBalanceError] =
    useState(null);


  // ==========================================================
  // BAIT PRICE
  // ==========================================================

  const [apiBaitPrice, setApiBaitPrice] =
    useState(null);

  const [priceLoading, setPriceLoading] =
    useState(false);

  const [priceError, setPriceError] =
    useState(null);


  // ==========================================================
  // DEBUG
  // ==========================================================

  useEffect(() => {

    console.log("====================================");
    console.log("WalletIcons DEBUG");
    console.log("Wallet:", wallet);
    console.log("Parent Balance:", balance);
    console.log("Parent BAIT Price:", baitPrice);
    console.log("Selected Chain:", selectedChain);
    console.log("Active Item:", activeItem);
    console.log("RPC Balance:", rpcBalance);
    console.log("API BAIT Price:", apiBaitPrice);
    console.log("====================================");

  }, [
    wallet,
    balance,
    baitPrice,
    selectedChain,
    activeItem,
    rpcBalance,
    apiBaitPrice
  ]);


  // ==========================================================
  // FORMAT ADDRESS
  // ==========================================================

  const formatAddress = (addr) => {

    if (!addr) {
      return "No wallet connected";
    }

    if (addr.length <= 10) {
      return addr;
    }

    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };


  // ==========================================================
  // FULL ADDRESS
  // ==========================================================

  const getFullAddress = (addr) => {

    if (!addr) {
      return "No wallet connected";
    }

    return addr;
  };


  // ==========================================================
  // COPY ADDRESS
  // ==========================================================

  const copyAddress = async () => {

    if (!wallet) {
      return;
    }

    try {

      await navigator.clipboard.writeText(wallet);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);

    } catch (error) {

      console.error(
        "Failed to copy address:",
        error
      );

    }
  };


  // ==========================================================
  // NUMERIC VALUE HELPER
  // ==========================================================

  const getNumericValue = useCallback((value) => {

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return 0;
    }


    if (typeof value === "number") {

      return Number.isFinite(value)
        ? value
        : 0;

    }


    if (typeof value === "bigint") {

      return Number(value);

    }


    if (typeof value === "string") {

      const cleaned =
        value
          .replace(/,/g, "")
          .trim();

      const number =
        Number(cleaned);

      return Number.isFinite(number)
        ? number
        : 0;

    }


    return 0;

  }, []);


  // ==========================================================
// FETCH BAIT BALANCE FROM GOLDCHAIN
// ==========================================================

const fetchBaitBalance = useCallback(
  async () => {

    if (!wallet) {

      setRpcBalance(null);
      setBalanceError(null);

      return;
    }


    // --------------------------------------------------------
    // Only fetch GoldChain balance for GoldChain
    // --------------------------------------------------------

    if (
      selectedChain &&
      selectedChain.toLowerCase() !==
        GOLDCHAIN_CHAIN_ID.toLowerCase()
    ) {

      console.log(
        "Skipping GoldChain balance because selected chain is:",
        selectedChain
      );

      return;
    }


    setBalanceLoading(true);
    setBalanceError(null);


    try {

      console.log(
        "Fetching BAIT balance for:",
        wallet
      );

      console.log(
        "GoldChain RPC:",
        GOLDCHAIN_RPC
      );


      const response = await fetch(
        GOLDCHAIN_RPC,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json"
          },

          body: JSON.stringify({

            jsonrpc: "2.0",

            method: "eth_getBalance",

            params: [
              wallet,
              "latest"
            ],

            id: Date.now()

          })
        }
      );


      if (!response.ok) {

        throw new Error(
          `RPC HTTP error: ${response.status}`
        );

      }


      const data =
        await response.json();


      console.log(
        "GoldChain balance RPC response:",
        data
      );


      if (data.error) {

        throw new Error(
          data.error.message ||
          "GoldChain RPC returned an error"
        );

      }


      if (!data.result) {

        throw new Error(
          "RPC did not return a balance"
        );

      }


      // ------------------------------------------------------
      // HEX WEI → BAIT
      // ------------------------------------------------------

      const weiNumber =
        Number.parseInt(
          data.result,
          16
        );


      if (!Number.isFinite(weiNumber)) {

        throw new Error(
          "Invalid balance returned by GoldChain RPC"
        );

      }


      const baitBalance =
        weiNumber / 1e18;


      console.log(
        "Converted BAIT balance:",
        baitBalance
      );


      setRpcBalance(
        Number.isFinite(baitBalance)
          ? baitBalance
          : 0
      );


    } catch (error) {

      console.error(
        "Failed to fetch BAIT balance:",
        error
      );


      setBalanceError(
        error.message ||
        "Unable to load balance"
      );


      setRpcBalance(null);


    } finally {

      setBalanceLoading(false);

    }

  },
  [
    wallet,
    selectedChain
  ]
);

  // ==========================================================
  // FETCH BAIT PRICE
  // ==========================================================

  const fetchBaitPrice = useCallback(
    async () => {

      setPriceLoading(true);

      setPriceError(null);


      try {

        console.log(
          "Fetching BAIT price from:",
          BAIT_PRICE_API
        );


        const response =
          await fetch(
            BAIT_PRICE_API,
            {
              method: "GET",

              headers: {
                Accept:
                  "application/json"
              }
            }
          );


        if (!response.ok) {

          throw new Error(
            `Price API HTTP error: ${response.status}`
          );

        }


        const data =
          await response.json();


        console.log(
          "BAIT Price API response:",
          data
        );


        // ----------------------------------------------------
        // YOUR API STRUCTURE:
        //
        // {
        //   "bait": {
        //      "symbol": "BAIT",
        //      "gold_backing_grams": 4.25,
        //      "price_usd": 600.2399
        //   }
        // }
        // ----------------------------------------------------

        const price =
          Number(
            data?.bait?.price_usd
          );


        if (
          !Number.isFinite(price) ||
          price <= 0
        ) {

          throw new Error(
            "Invalid BAIT price returned by API"
          );

        }


        console.log(
          "Converted BAIT price:",
          price
        );


        setApiBaitPrice(price);

      } catch (error) {

        console.error(
          "Failed to fetch BAIT price:",
          error
        );


        setApiBaitPrice(null);

        setPriceError(
          error.message ||
          "Unable to load BAIT price"
        );

      } finally {

        setPriceLoading(false);

      }

    },
    []
  );


  // ==========================================================
  // INITIAL BALANCE FETCH
  // ==========================================================

  useEffect(() => {

    if (!wallet) {

      setRpcBalance(null);

      return;

    }


    fetchBaitBalance();

  }, [
    wallet,
    selectedChain,
    fetchBaitBalance
  ]);


  // ==========================================================
  // BALANCE AUTO REFRESH
  // ==========================================================

  useEffect(() => {

    if (!wallet) {
      return undefined;
    }


    const interval =
      setInterval(
        () => {

          fetchBaitBalance();

        },
        15000
      );


    return () => {

      clearInterval(interval);

    };

  }, [
    wallet,
    selectedChain,
    fetchBaitBalance
  ]);


  // ==========================================================
  // INITIAL PRICE FETCH
  // ==========================================================

  useEffect(() => {

    fetchBaitPrice();

  }, [
    fetchBaitPrice
  ]);


  // ==========================================================
  // PRICE AUTO REFRESH
  // ==========================================================

  useEffect(() => {

    const interval =
      setInterval(
        () => {

          fetchBaitPrice();

        },
        60000
      );


    return () => {

      clearInterval(interval);

    };

  }, [
    fetchBaitPrice
  ]);


  // ==========================================================
  // DISPLAY BALANCE
  // ==========================================================

  const displayBalance = useMemo(() => {

    // Prefer directly fetched GoldChain balance
    if (
      rpcBalance !== null &&
      rpcBalance !== undefined &&
      Number.isFinite(
        rpcBalance
      )
    ) {

      return rpcBalance;

    }


    // Fall back to App.js balance
    return getNumericValue(
      balance
    );

  }, [
    rpcBalance,
    balance,
    getNumericValue
  ]);


  // ==========================================================
  // DISPLAY BAIT PRICE
  // ==========================================================

  const numericBaitPrice = useMemo(() => {

    // --------------------------------------------------------
    // First preference:
    // Price fetched directly from API
    // --------------------------------------------------------

    if (
      apiBaitPrice !== null &&
      apiBaitPrice !== undefined &&
      Number.isFinite(
        apiBaitPrice
      ) &&
      apiBaitPrice > 0
    ) {

      return apiBaitPrice;

    }


    // --------------------------------------------------------
    // Second preference:
    // baitPrice supplied by App.js
    // --------------------------------------------------------

    const parentPrice =
      getNumericValue(
        baitPrice
      );


    return parentPrice;

  }, [
    apiBaitPrice,
    baitPrice,
    getNumericValue
  ]);


  // ==========================================================
  // PORTFOLIO VALUE
  // ==========================================================

  const portfolioValue = useMemo(() => {

    if (
      !Number.isFinite(
        displayBalance
      )
    ) {

      return 0;

    }


    if (
      !Number.isFinite(
        numericBaitPrice
      )
    ) {

      return 0;

    }


    return (
      displayBalance *
      numericBaitPrice
    );

  }, [
    displayBalance,
    numericBaitPrice
  ]);


  // ==========================================================
  // ACTIVE NAVIGATION
  // ==========================================================

  const currentActiveItem =
    useMemo(() => {

      const path =
        location.pathname;


      switch (path) {

        case "/dashboard":
          return "dashboard";

        case "/yourwallet":
          return "wallet";

        case "/swap":
          return "swap";

        case "/bridge":
          return "bridge";

        case "/send":
          return "transfer";

        case "/receive":
          return "receive";

        case "/market":
          return "markets";

        case "/buysell":
          return "buy";

        case "/activity":
          return "activity";

        case "/settings":
          return "settings";

        default:
          return (
            activeItem ||
            "wallet"
          );

      }

    }, [
      location.pathname,
      activeItem
    ]);


  // ==========================================================
  // NAVIGATION ITEMS
  // ==========================================================

  const menuItems = [

    {
      icon: LayoutDashboard,
      text: "Home",
      action: "dashboard"
    },

    {
      icon: Wallet,
      text: "Wallet",
      action: "wallet"
    },

    {
      icon: TrendingUp,
      text: "Markets",
      action: "markets"
    },

    {
      icon: ArrowLeftRight,
      text: "Swap",
      action: "swap"
    },

    {
      icon: GitBranch,
      text: "Bridge",
      action: "bridge"
    },

    {
      icon: Send,
      text: "Send",
      action: "transfer"
    },

    {
      icon: Download,
      text: "Receive",
      action: "receive"
    },

    {
      icon: ShoppingBag,
      text: "Buy Crypto",
      action: "buy"
    },

    {
      icon: Activity,
      text: "Activity",
      action: "activity"
    },

    {
      icon: Settings,
      text: "Settings",
      action: "settings"
    }

  ];


  // ==========================================================
  // NAVIGATION HANDLER
  // ==========================================================

  const handleNavigation =
    (item) => {

      setSidebarOpen(false);


      console.log(
        "Navigating:",
        item.text,
        "→",
        item.action
      );


      if (
        onNavigate &&
        item.action
      ) {

        onNavigate(
          item.action
        );

      }

    };


  // ==========================================================
  // FORMAT BAIT BALANCE
  // ==========================================================

  const formattedBaitBalance =
    displayBalance.toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      }
    );


  // ==========================================================
  // FORMAT PORTFOLIO
  // ==========================================================

  const formattedPortfolio =
    portfolioValue.toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div className="wallet-icons-container">


      {/* =====================================================
          HAMBURGER
      ===================================================== */}

      <button
        className={`hamburger-btn ${
          sidebarOpen
            ? "hidden"
            : ""
        }`}
        onClick={() =>
          setSidebarOpen(
            !sidebarOpen
          )
        }
        aria-label="Open wallet menu"
      >

        <Menu size={22} />

      </button>


      {/* =====================================================
          OVERLAY
      ===================================================== */}

      <div
        className={`sidebar-overlay ${
          sidebarOpen
            ? "open"
            : ""
        }`}
        onClick={() =>
          setSidebarOpen(false)
        }
      />


      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      <div
        className={`sidebar-menu ${
          sidebarOpen
            ? "open"
            : ""
        }`}
      >


        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="sidebar-header">

          <div className="sidebar-logo">

            <img
              src="/BaitLogo.png"
              alt="BAIT PAY"
              className="sidebar-logo-img"

              onError={(e) => {

                e.target.onerror =
                  null;

                e.target.src =
                  require("../BaitLogo.png");

              }}
            />

            <h3>
              BAIT PAY
            </h3>

          </div>


          <button
            className="sidebar-close"
            onClick={() =>
              setSidebarOpen(false)
            }
            aria-label="Close menu"
          >

            <X size={20} />

          </button>

        </div>


        {/* ===================================================
            WALLET INFORMATION
        =================================================== */}

        <div className="sidebar-wallet-info">

          <div className="sidebar-address-wrapper">

            <div className="sidebar-address">

              {wallet
                ? formatAddress(wallet)
                : "No wallet connected"
              }


              {wallet && (

                <button
                  className="copy-btn"
                  onClick={copyAddress}
                  title="Copy address"
                >

                  {copied
                    ? <CheckCircle size={14} />
                    : <Copy size={14} />
                  }

                </button>

              )}

            </div>


            {wallet && (

              <div
                className="sidebar-full-address"
                title={getFullAddress(wallet)}
              >

                {getFullAddress(
                  wallet
                )}

              </div>

            )}

          </div>


          <div className="sidebar-status">

            <span
              className={`status-dot ${
                wallet
                  ? ""
                  : "disconnected"
              }`}
            />


            {wallet
              ? "Connected"
              : "Disconnected"
            }


            {selectedChain &&
              wallet && (

              <span
                className="sidebar-chain-badge"
              >

                {selectedChain ===
                  GOLDCHAIN_CHAIN_ID
                  ? "GoldChain"
                  : selectedChain
                }

              </span>

            )}

          </div>

        </div>


        {/* ===================================================
            PORTFOLIO
        =================================================== */}

        <div className="sidebar-portfolio">

          <div className="portfolio-label">
            Portfolio Value
          </div>


          <div className="portfolio-value">

            {balanceLoading &&
             rpcBalance === null ? (

              "Loading..."

            ) : (

              <>
                ${formattedPortfolio}
              </>

            )}

          </div>


          {/* =================================================
              BAIT BALANCE
          ================================================= */}

          <div
            className="portfolio-change"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "3px"
            }}
          >

            <span>
              {formattedBaitBalance} BAIT
            </span>


            {/* -----------------------------------------------
                PRICE
            ----------------------------------------------- */}

            {numericBaitPrice > 0 ? (

              <span className="change-time">

                BAIT Price: $
                {numericBaitPrice.toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4
                  }
                )}

              </span>

            ) : priceLoading ? (

              <span className="change-time">

                Loading BAIT price...

              </span>

            ) : (

              <span className="change-time">

                BAIT price unavailable

              </span>

            )}

          </div>


          {/* =================================================
              BALANCE ERROR
          ================================================= */}

          {balanceError && (

            <div
              className="change-time"
              style={{
                marginTop: "5px",
                color: "#b91c1c"
              }}
            >

              Balance RPC error

            </div>

          )}


          {/* =================================================
              PRICE ERROR
          ================================================= */}

          {priceError && (

            <div
              className="change-time"
              style={{
                marginTop: "5px",
                color: "#b91c1c"
              }}
            >

              Price API error

            </div>

          )}

        </div>


        {/* ===================================================
            NAVIGATION
        =================================================== */}

        <nav className="sidebar-nav">

          {menuItems.map(
            (item, index) => {

              const Icon =
                item.icon;


              return (

                <div
                  key={index}

                  className={`nav-item ${
                    currentActiveItem ===
                    item.action
                      ? "active"
                      : ""
                  }`}

                  onClick={() =>
                    handleNavigation(
                      item
                    )
                  }
                >

                  <Icon
                    className="nav-icon"
                    size={20}
                  />

                  <span>
                    {item.text}
                  </span>

                </div>

              );

            }
          )}

        </nav>


        {/* ===================================================
            FOOTER
        =================================================== */}

        <div className="sidebar-footer">


          {/* =================================================
              NETWORK
          ================================================= */}

          <div className="sidebar-network-card">

            <div className="network-left">

              <span className="network-dot" />

              <span className="chain-name">

                {selectedChain ===
                  GOLDCHAIN_CHAIN_ID
                  ? "GoldChain"
                  : selectedChain ||
                    "GoldChain"
                }

              </span>

            </div>


            <div className="network-right">

              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >

                <path
                  d="M4 20H7V15H4V20ZM10 20H13V11H10V20ZM16 20H19V6H16V20Z"
                  fill="#22c55e"
                />

              </svg>

            </div>

          </div>


          {/* =================================================
              THEME
          ================================================= */}

         


        </div>


      </div>

    </div>

  );

};


export default WalletIcons;